import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material'
import { useField, useFormikContext } from 'formik'

import rem from '~/utils/ui'

export type TAssistantOption = {
  label: string
  value: string
}

type TProps = {
  isLoading: boolean
  label?: string
  limitTags?: number
  name: string
  options: TAssistantOption[]
  unavailableValues?: string[]
}

function AssistantMultiSelect({
  isLoading,
  label = '',
  limitTags = 1,
  name,
  options,
  unavailableValues = [],
}: TProps) {
  const [field, meta, helpers] = useField<string[]>(name)
  const { isSubmitting } = useFormikContext()
  const unavailableValueSet = new Set(unavailableValues)
  const selectedOptions = options.filter((option) =>
    (field.value ?? []).includes(option.value),
  )
  const hasError = meta.touched && typeof meta.error === 'string'

  return (
    <FormControl error={hasError} fullWidth sx={{ gap: rem(8) }}>
      {!!label && (
        <FormLabel
          sx={{
            '&, &.Mui-focused': {
              fontSize: rem(14),
              fontWeight: 500,
            },
          }}
        >
          {label}
        </FormLabel>
      )}
      <Autocomplete
        disableCloseOnSelect
        disabled={isLoading || isSubmitting}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => unavailableValueSet.has(option.value)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        limitTags={limitTags}
        loading={isLoading}
        multiple
        onBlur={() => helpers.setTouched(true)}
        onChange={(_event, selected) => {
          helpers.setValue(selected.map((option) => option.value))
        }}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            error={hasError}
            helperText={hasError ? meta.error : undefined}
            placeholder={selectedOptions.length ? '' : 'Select assistants'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && (
                    <CircularProgress color="inherit" size={rem(16)} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              minWidth: rem(210),
              '& .MuiInputBase-root': {
                minHeight: rem(36),
                paddingBlock: '0 !important',
              },
              '& .MuiChip-root': {
                height: rem(24),
                maxWidth: rem(130),
              },
            }}
          />
        )}
        renderOption={({ key, ...props }, option) => (
          <li {...props} key={key}>
            <Typography noWrap variant="body2">
              {option.label}
            </Typography>
          </li>
        )}
        value={selectedOptions}
      />
    </FormControl>
  )
}

export default AssistantMultiSelect
